// ─────────────────────────────────────────────
// KLOTHIQ — Cash Accounts Page (wired)
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Select } from '@/components/ui/FormControls';
import { useState } from 'react';
import {
  useCashAccounts,
  useCashTransactions,
  useDeleteCashAccount,
  useVoidCashTransaction,
  useCreateCashAccount,
  splitBalances,
  CashAccount,
  CashTransaction,
} from '@/hooks/useCash';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import EmptyState from '@/components/common/EmptyState';
import CashStatementExports from '@/components/cash/CashStatementExports';
import ReceiptPreviewModal from '@/components/common/ReceiptPreviewModal';
import UpdateBankBalanceModal from '@/pages/accounts/components/UpdateBankBalanceModal';
import { PaymentReceiptData } from '@/utils/receiptGenerator';
import { toast } from 'sonner';
import { useConfirm } from '@/contexts/ConfirmContext';
import {
  Wallet,
  Landmark,
  Smartphone,
  MoreHorizontal,
  TrendingUp,
  TrendingDown,
  Trash2,
  FileText,
  Plus,
  X,
  Loader2,
} from 'lucide-react';

function formatRupee(val: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(val);
}

const ACCOUNT_ICONS: Record<string, React.ReactNode> = {
  cash:  <Wallet className="h-5 w-5" />,
  bank:  <Landmark className="h-5 w-5" />,
  upi:   <Smartphone className="h-5 w-5" />,
  other: <MoreHorizontal className="h-5 w-5" />,
};

interface CashAccountsPageProps {
  embedded?: boolean;
}

export default function CashAccountsPage({ embedded = false }: CashAccountsPageProps) {
  const { can } = useAuth();
  const isOwnerOrAdmin = can('accounts_balances.manage');

  const { data: accounts, isLoading, error, refetch } = useCashAccounts();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTxnForReceipt, setSelectedTxnForReceipt] = useState<PaymentReceiptData | null>(null);

  const deleteAccount = useDeleteCashAccount();
  const voidTransaction = useVoidCashTransaction();
  const createAccount = useCreateCashAccount();
  const confirm = useConfirm();

  // Create account modal state
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [isUpdateBankOpen, setIsUpdateBankOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'cash' | 'bank' | 'upi' | 'other'>('bank');
  const [newOpeningBalance, setNewOpeningBalance] = useState('0.00');
  const [creatingAccount, setCreatingAccount] = useState(false);

  // KLOTHIQ Bank vs. sales (Main Cash / UPI) — kept separate, never combined (FR-023)
  const {
    bank: klothiqAccount,
    bankBalance,
    others: otherAccounts,
    salesBalance,
  } = splitBalances(accounts);

  const selectedAccount = accounts?.find((a) => a.id === selectedId) ?? klothiqAccount ?? accounts?.[0] ?? null;
  const { data: txns, isLoading: txnLoading, error: txnError, refetch: refetchTxns } = useCashTransactions(selectedAccount?.id);

  const totalBalance = (accounts ?? []).reduce((sum, a) => sum + Number(a.current_balance), 0);

  const handleDeleteAccount = async (account: CashAccount) => {
    const isOk = await confirm({
      title: 'Delete Cash Account',
      description: `Are you sure you want to delete/archive Cash Account "${account.name}"? (Current Balance: ₹${Number(account.current_balance).toFixed(2)})`,
      variant: 'destructive',
      confirmText: 'Delete Account',
    });
    if (!isOk) return;
    try {
      await deleteAccount.mutateAsync({ account_id: account.id });
      toast.success(`Cash Account "${account.name}" deleted successfully`);
      refetch();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete account');
    }
  };

  const handleVoidTransaction = async (txn: CashTransaction) => {
    const amountStr = Number(txn.amount) >= 0 ? `+₹${Number(txn.amount).toFixed(2)}` : `-₹${Math.abs(Number(txn.amount)).toFixed(2)}`;
    const isOk = await confirm({
      title: 'Void Transaction',
      description: `Are you sure you want to void/delete transaction "${txn.particular}" (${amountStr})?\n\nThis will automatically reverse its effect on the current account balance.`,
      variant: 'warning',
      confirmText: 'Void Transaction',
    });
    if (!isOk) return;
    try {
      await voidTransaction.mutateAsync({ transaction_id: txn.id, reason: 'Voided by user from Cash Accounts' });
      toast.success(`Transaction "${txn.particular}" voided successfully`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to void transaction');
    }
  };

  if (isLoading) return <LoadingState message="Loading accounts…" />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error('Account name is required');
      return;
    }
    const balNum = parseFloat(newOpeningBalance);
    if (isNaN(balNum) || balNum < 0) {
      toast.error('Opening balance must be a non-negative number');
      return;
    }

    setCreatingAccount(true);
    try {
      await createAccount.mutateAsync({
        name: newName.trim(),
        account_type: newType,
        opening_balance: balNum,
      });
      toast.success(`Account "${newName.trim()}" created successfully`);
      setIsAddAccountOpen(false);
      setNewName('');
      setNewOpeningBalance('0.00');
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  const exportComponent = <CashStatementExports accountId={selectedAccount?.id} disabled={txnLoading} />;

  const actionsComponent = (
    <div className="flex items-center gap-2">
      {isOwnerOrAdmin && (
        <>
          <button
            type="button"
            onClick={() => setIsUpdateBankOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-emerald-700 transition-colors"
          >
            <Landmark className="h-3.5 w-3.5" />
            <span>Update Bank Balance</span>
          </button>
          {!embedded && (
            <button
              type="button"
              onClick={() => setIsAddAccountOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-xs hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>+ Add Account</span>
            </button>
          )}
        </>
      )}
      {exportComponent}
    </div>
  );

  return (
    <div className="space-y-4">
      {!embedded ? (
        <PageHeader
          title="Cash Accounts"
          description="Cash, bank and UPI balances with full transaction ledger"
          actions={actionsComponent}
        />
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">KLOTHIQ Bank available balance and live statement</p>
          {actionsComponent}
        </div>
      )}

      {/* Account cards */}
      {accounts && accounts.length > 0 ? (
        <>
          {embedded ? (
            /* Unified KLOTHIQ Bank balance display (FR-021, FR-024) */
            <div className={`mb-4 grid grid-cols-1 gap-3 ${otherAccounts.length > 0 ? 'lg:grid-cols-2' : ''}`}>
              {/* KLOTHIQ Bank */}
              <div
                onClick={() => klothiqAccount && setSelectedId(klothiqAccount.id)}
                className={`obsidian-surface cursor-pointer rounded-xl border bg-gradient-to-br from-emerald-500/10 via-background to-card p-5 shadow-sm transition-all ${
                  klothiqAccount && selectedAccount?.id === klothiqAccount.id
                    ? 'border-emerald-500/60 ring-1 ring-emerald-500/30'
                    : 'border-emerald-500/30 hover:border-emerald-500/50'
                }`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3.5">
                    <div className="rounded-xl bg-emerald-500/20 p-3 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30">
                      <Landmark className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-foreground">KLOTHIQ Bank</h2>
                      <p className="mt-0.5 text-xs text-muted-foreground">Total available balance in the bank account (includes sales)</p>
                      <div className="mt-2 text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                        {formatRupee(bankBalance)}
                      </div>
                    </div>
                  </div>
                  {isOwnerOrAdmin && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsUpdateBankOpen(true);
                      }}
                      className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors"
                    >
                      <Landmark className="h-4 w-4" />
                      <span>Update Bank Balance</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Other legacy accounts (if any exist) */}
              {otherAccounts.length > 0 && (
                <div className="obsidian-surface rounded-xl border border-border/80 bg-card p-5 shadow-sm">
                  <div className="flex items-start gap-3.5">
                    <div className="rounded-xl bg-muted p-3 text-muted-foreground">
                      <Wallet className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-bold text-foreground">Other Accounts</h2>
                      <p className="mt-0.5 text-xs text-muted-foreground">Legacy accounts balance</p>
                      <div className="mt-2 text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                        {formatRupee(salesBalance)}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {otherAccounts.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => setSelectedId(a.id)}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                              selectedAccount?.id === a.id
                                ? 'border-primary/60 bg-primary/10 text-foreground'
                                : 'border-border bg-card text-muted-foreground hover:text-foreground'
                            }`}
                            title={`Show ${a.name} ledger`}
                          >
                            {a.name}: <span className="font-semibold">{formatRupee(Number(a.current_balance))}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {accounts.map((a) => (
                <div
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  className={`obsidian-surface group relative rounded-lg border p-4 text-left shadow-sm transition-all hover:shadow-md cursor-pointer ${
                    (selectedId ?? accounts[0]?.id) === a.id
                      ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border bg-card'
                  }`}
                >
                  <div className="flex items-center justify-between text-muted-foreground">
                    <div className="flex items-center gap-2">
                      {ACCOUNT_ICONS[a.account_type] ?? ACCOUNT_ICONS.other}
                      <span className="text-xs font-medium uppercase tracking-wider">{a.account_type}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAccount(a);
                      }}
                      className="rounded p-1 opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                      title="Delete / Archive Account"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-2 truncate text-sm font-semibold text-foreground">{a.name}</p>
                  <p className={`mt-1 text-lg font-bold ${Number(a.current_balance) < 0 ? 'text-destructive' : 'text-foreground'}`}>
                    {formatRupee(Number(a.current_balance))}
                  </p>
                </div>
              ))}

              {/* Total card */}
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total Balance</p>
                <p className="mt-3 text-xl font-bold text-foreground">{formatRupee(totalBalance)}</p>
              </div>
            </div>
          )}

          {/* Transaction ledger */}
          {selectedAccount && (
            <div className="obsidian-surface rounded-lg border border-border bg-card shadow-sm">
              <div className="border-b border-border px-4 py-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {selectedAccount.name} — Ledger
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Recent entries are shown below. PDF and Excel include the full active ledger and a fresh account balance.
                </p>
              </div>

              {txnLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading transactions…</div>
              ) : txnError ? (
                <ErrorState message="The account ledger could not be loaded." onRetry={refetchTxns} />
              ) : !txns || txns.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">No transactions yet</div>
              ) : (
                <>
                  {/* Desktop */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border bg-muted/50">
                        <tr>
                          <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Date</th>
                          <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Particular</th>
                          <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Type</th>
                          <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">In (₹)</th>
                          <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Out (₹)</th>
                          <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Balance (₹)</th>
                          <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {txns.map((t) => (
                          <tr key={t.id} className="hover:bg-muted/20">
                            <td className="px-4 py-2.5 text-muted-foreground">{t.transaction_date}</td>
                            <td className="px-4 py-2.5 text-foreground">{t.particular}</td>
                            <td className="px-4 py-2.5">
                              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t.transaction_type.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              {Number(t.amount) > 0 ? (
                                <span className="flex items-center justify-end gap-1 font-medium text-emerald-600">
                                  <TrendingUp className="h-3 w-3" />
                                  {formatRupee(Number(t.amount))}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              {Number(t.amount) < 0 ? (
                                <span className="flex items-center justify-end gap-1 font-medium text-destructive">
                                  <TrendingDown className="h-3 w-3" />
                                  {formatRupee(Math.abs(Number(t.amount)))}
                                </span>
                              ) : '—'}
                            </td>
                             <td className="px-4 py-2.5 text-right font-mono font-semibold text-foreground">
                              {formatRupee(Number(t.balance_after))}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => setSelectedTxnForReceipt({
                                    receipt_number: `VCH-${t.id.slice(0, 8).toUpperCase()}`,
                                    payment_date: t.transaction_date,
                                    payment_type: Number(t.amount) >= 0 ? 'income' : 'expense',
                                    party_name: selectedAccount?.name || 'General',
                                    amount: Math.abs(Number(t.amount)),
                                    payment_method: selectedAccount?.account_type || 'cash',
                                    account_name: selectedAccount?.name,
                                    particular: t.particular,
                                    reference_number: t.reference_id,
                                  })}
                                  className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                                  title="View Voucher / Receipt"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleVoidTransaction(t)}
                                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                  title="Void / Delete Transaction"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile */}
                  <div className="divide-y divide-border md:hidden">
                    {txns.map((t) => (
                      <div key={t.id} className="flex items-center justify-between px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm text-foreground">{t.particular}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{t.transaction_date}</p>
                        </div>
                        <div className="ml-3 shrink-0 flex items-center gap-2 text-right">
                          <div>
                            <p className={`text-sm font-semibold ${Number(t.amount) >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                              {Number(t.amount) >= 0 ? '+' : ''}{formatRupee(Number(t.amount))}
                            </p>
                            <p className="text-xs text-muted-foreground">Bal: {formatRupee(Number(t.balance_after))}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedTxnForReceipt({
                              receipt_number: `VCH-${t.id.slice(0, 8).toUpperCase()}`,
                              payment_date: t.transaction_date,
                              payment_type: Number(t.amount) >= 0 ? 'income' : 'expense',
                              party_name: selectedAccount?.name || 'General',
                              amount: Math.abs(Number(t.amount)),
                              payment_method: selectedAccount?.account_type || 'cash',
                              account_name: selectedAccount?.name,
                              particular: t.particular,
                              reference_number: t.reference_id,
                            })}
                            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                            title="View Voucher"
                          >
                            <FileText className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVoidTransaction(t)}
                            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            title="Void / Delete Transaction"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon={<Wallet className="h-12 w-12" />}
          title="No cash accounts"
          description="Cash accounts are created automatically from seed data. Apply the migrations to get started."
        />
      )}

      {/* Payment Receipt Voucher Modal */}
      {selectedTxnForReceipt && (
        <ReceiptPreviewModal
          isOpen={!!selectedTxnForReceipt}
          onClose={() => setSelectedTxnForReceipt(null)}
          paymentData={selectedTxnForReceipt}
        />
      )}

      {/* Add Account Modal */}
      {isAddAccountOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs" data-field-guide-scope>
          <div className="obsidian-panel relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95">
            <button
              type="button"
              onClick={() => setIsAddAccountOpen(false)}
              disabled={creatingAccount}
              className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-start justify-between gap-3 pr-8">
              <h2 className="text-lg font-semibold text-foreground">Add Cash / Bank Account</h2>
              <UserGuide compact />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Create a new ledger account for tracking cash, bank balances or UPI
            </p>

            <form onSubmit={handleCreateAccount} noValidate className="mt-5 space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="acc-name" className="text-xs font-medium text-foreground">
                  Account Name <span className="text-destructive">*</span>
                </label>
                <Input
                  id="acc-name"
                  type="text"
                  placeholder="e.g. HDFC Current Account, Shop Cash Drawer"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={creatingAccount}
                  helpLabel="Account Name *"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="acc-type" className="text-xs font-medium text-foreground">
                  Account Type <span className="text-destructive">*</span>
                </label>
                <Select
                  id="acc-type"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as any)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={creatingAccount}
                  helpLabel="Account Type *"
                >
                  <option value="bank">Bank Account</option>
                  <option value="cash">Cash in Hand</option>
                  <option value="upi">UPI Account</option>
                  <option value="other">Other</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="acc-opening-bal" className="text-xs font-medium text-foreground">
                  Initial Opening Balance (₹)
                </label>
                <Input
                  id="acc-opening-bal"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newOpeningBalance}
                  onChange={(e) => setNewOpeningBalance(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={creatingAccount}
                  helpLabel="Initial Opening Balance (₹)"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-3">
                <button
                  type="button"
                  onClick={() => setIsAddAccountOpen(false)}
                  disabled={creatingAccount}
                  className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingAccount}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {creatingAccount && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Bank Balance Modal (FR-022) */}
      <UpdateBankBalanceModal
        isOpen={isUpdateBankOpen}
        onClose={() => setIsUpdateBankOpen(false)}
        currentBalance={Number(klothiqAccount?.current_balance ?? 0)}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
